import { routeTree } from "@/routeTree.gen"
import { ensureBotIdInitialized } from "@/lib/utils/botid-client"
import { getSentryClientConfig } from "@/lib/utils/sentry"
import { analyticsAllowed, onConsentChange } from "@/lib/utils/consent"
import { QueryClient } from "@tanstack/react-query"
import { createRouter as createTanStackRouter } from "@tanstack/react-router"
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query"
import { lazy } from "react"

const NotFound = lazy(() => import("@/components/not-found"))

const DEFAULT_COUNTRY_CODE = "us"
const COUNTRY_CODE_PREFIX = /^\/[a-z]{2}(\/|$)/
const STATIC_FILE_PATH = /\.[a-zA-Z0-9]+$/
const NO_COUNTRY_REWRITE_EXACT = new Set([
  "/favicon.ico",
  "/health",
  "/robots.txt",
  "/sitemap.xml",
])
const NO_COUNTRY_REWRITE_PREFIXES = [
  "/_server",
  "/api",
  "/assets",
  "/images",
  "/medusa",
  "/seasonal",
]
let sentryInitialized = false

// Sentry's chunk (~157 KB, mostly replay/feedback code) used to download on the
// first idle moment after hydration, which is often before the page has
// painted. Lighthouse then counts it against First Contentful Paint. Wait for
// the browser to report FCP, then idle; the timeout covers browsers without
// paint timing or a tab that is hidden during load.
function scheduleClientSentryInit(callback: () => void) {
  if (typeof window === "undefined") return

  let started = false
  const runWhenIdle = () => {
    if (started) return
    started = true
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(callback, { timeout: 3000 })
    } else {
      globalThis.setTimeout(callback, 500)
    }
  }

  globalThis.setTimeout(runWhenIdle, 6000)

  try {
    const observer = new PerformanceObserver((list) => {
      if (list.getEntriesByName("first-contentful-paint").length > 0) {
        observer.disconnect()
        runWhenIdle()
      }
    })
    observer.observe({ type: "paint", buffered: true })
  } catch {
    runWhenIdle()
  }
}

function shouldRewriteToDefaultCountry(pathname: string) {
  if (NO_COUNTRY_REWRITE_EXACT.has(pathname)) return false
  if (STATIC_FILE_PATH.test(pathname)) return false
  if (COUNTRY_CODE_PREFIX.test(pathname)) return false

  return !NO_COUNTRY_REWRITE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

function stripDefaultCountry(pathname: string) {
  const prefix = `/${DEFAULT_COUNTRY_CODE}`

  if (pathname === prefix || pathname === `${prefix}/`) return "/"
  if (pathname.startsWith(`${prefix}/`)) return pathname.slice(prefix.length) || "/"

  return pathname
}

export function createRouter() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // Optimize for SSR - shorter stale time for fresh data
        staleTime: 1000 * 60, // 1 minute
        // Enable refetch on window focus for fresh data
        refetchOnWindowFocus: true,
        // Enable refetch on reconnect
        refetchOnReconnect: true,
        // Retry failed requests
        retry: 1,
      },
    },
  })

  const router = createTanStackRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: false, // SSR handles data fetching on the server
    defaultNotFoundComponent: NotFound,
    scrollRestoration: true,
    rewrite: {
      input: ({ url }) => {
        if (shouldRewriteToDefaultCountry(url.pathname)) {
          url.pathname = `/${DEFAULT_COUNTRY_CODE}${url.pathname === "/" ? "" : url.pathname}`
        }
        return url
      },
      output: ({ url }) => {
        url.pathname = stripDefaultCountry(url.pathname)
        return url
      },
    },
  })
  setupRouterSsrQueryIntegration({
    router,
    queryClient,
  })

  if (!router.isServer) {
    ensureBotIdInitialized()
  }

  if (!router.isServer && !sentryInitialized) {
    const sentryConfig = getSentryClientConfig()

    if (sentryConfig) {
      sentryInitialized = true

      scheduleClientSentryInit(() => {
        void import("@/lib/utils/sentry-client")
          .then(({ addIntegration, init, tanstackRouterBrowserTracingIntegration }) => {
            init({
              ...sentryConfig,
              integrations: [tanstackRouterBrowserTracingIntegration(router)],
            })

            // Session replay records interaction (the highest-litigated
            // tracking category), so it is gated behind analytics consent and
            // its code is only downloaded once that consent exists. Error +
            // performance monitoring stays on as operational tooling.
            let replayRequested = false
            const addReplay = () => {
              if (replayRequested) return
              replayRequested = true
              void import("@/lib/utils/sentry-replay")
                .then(({ replayIntegration }) => addIntegration(replayIntegration()))
                .catch(() => {
                  /* replay stays off; errors are still reported */
                })
            }

            if (analyticsAllowed()) {
              addReplay()
            } else {
              onConsentChange((state) => {
                if (state.analytics === "granted") addReplay()
              })
            }
          })
          .catch(() => {
            if (import.meta.env.DEV) {
              console.error("[+Bake Cafe] Failed to initialize Sentry")
            }
          })
      })
    }
  }

  return router
}

export function getRouter() {
  return createRouter()
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createRouter>;
  }
}
